import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Single toast renderer for the whole app.
 *
 * Styling uses semantic design tokens so it themes correctly on the public
 * site and inside the dark portals. Variants are distinguished by a coloured
 * left rail + icon rather than by repainting the whole surface, so several
 * stacked toasts stay readable.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group z-[100000]"
      style={{ zIndex: 100000 }}
      position="top-center"
      visibleToasts={4}
      duration={4000}
      gap={8}
      expand
      offset="calc(env(safe-area-inset-top, 0px) + 16px)"
      mobileOffset="calc(env(safe-area-inset-top, 0px) + 12px)"
      toastOptions={{
        classNames: {
          toast: [
            "group toast pointer-events-auto w-full max-w-[calc(100vw-24px)] sm:max-w-[420px] rounded-none",
            "border border-border border-l-4 border-l-border !bg-popover !text-popover-foreground shadow-lg",
            "gap-3 px-4 py-3 pr-10 text-sm",
          ].join(" "),
          content: "group-[.toast]:min-w-0 group-[.toast]:flex-1",
          title: "group-[.toast]:block group-[.toast]:break-words group-[.toast]:font-semibold group-[.toast]:leading-snug group-[.toast]:text-popover-foreground",
          description:
            "group-[.toast]:block group-[.toast]:break-words group-[.toast]:text-muted-foreground group-[.toast]:text-[13px] group-[.toast]:leading-snug group-[.toast]:mt-0.5",
          icon: "group-[.toast]:shrink-0",
          success: "group-[.toaster]:border-l-[hsl(var(--success,152_60%_38%))] [&_[data-icon]]:text-[hsl(var(--success,152_60%_38%))]",
          error: "group-[.toaster]:border-l-destructive [&_[data-icon]]:text-destructive",
          warning: "group-[.toaster]:border-l-[hsl(38_92%_50%)] [&_[data-icon]]:text-[hsl(38_92%_50%)]",
          info: "group-[.toaster]:border-l-primary [&_[data-icon]]:text-primary",
          loading: "group-[.toaster]:border-l-muted-foreground",
          closeButton: [
            "group-[.toast]:!bg-transparent group-[.toast]:!border-0 group-[.toast]:!shadow-none",
            "group-[.toast]:!text-muted-foreground group-[.toast]:hover:!text-foreground",
            "group-[.toast]:!left-auto group-[.toast]:!right-1.5 group-[.toast]:!top-1.5",
            "[&_svg]:!w-4 [&_svg]:!h-4",
          ].join(" "),
          actionButton:
            "group-[.toast]:rounded-none group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:font-semibold",
          cancelButton:
            "group-[.toast]:rounded-none group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      closeButton
      {...props}
    />
  );
};

export { Toaster, toast };
